import { Money } from '../components/ui/Money';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Vault, AlertTriangle, Loader2, CheckCircle2, Clock, PackageCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;
const aedText = (v) => `AED ${Math.round(Number(v || 0)).toLocaleString('en-US')}`;
const aed = (v) => <Money value={v} />;

const StatCard = ({ label, value, accent, testid }) => (
  <Card data-testid={testid}>
    <CardContent className="p-4">
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`text-2xl font-bold ${accent || 'text-gray-900'}`}>{value}</p>
    </CardContent>
  </Card>
);

const StaffVault = () => {
  const [data, setData] = useState({ rows: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(null);
  const [releaseTarget, setReleaseTarget] = useState(null);
  const [releasedTo, setReleasedTo] = useState('');
  const [releaseNotes, setReleaseNotes] = useState('');
  const [releasing, setReleasing] = useState(false);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/api/staff-vault`)
      .then((r) => setData(r.data || { rows: [], totals: {} }))
      .catch(() => setData({ rows: [], totals: {} }))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const cancelVisa = async (employeeId) => {
    setCancelling(employeeId);
    try {
      await axios.post(`${API}/api/staff-vault/cancel-visa`, { employee_id: employeeId });
      toast({ title: 'Visa cancelled', description: 'Status set to "Visa Cancelled Outside Country".' });
      load();
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setCancelling(null);
    }
  };

  const submitRelease = async () => {
    if (!releaseTarget) return;
    setReleasing(true);
    try {
      const res = await axios.post(`${API}/api/staff-vault/release`, {
        employee_id: releaseTarget.employee_id,
        released_to: releasedTo.trim() || null,
        notes: releaseNotes.trim() || null,
      });
      const restored = res?.data?.eosb_restored;
      toast({
        title: 'Released from vault',
        description: `${releaseTarget.employee_name}'s dues marked released${releasedTo ? ` to ${releasedTo}` : ''}.`
          + (restored ? ' Their EOSB is restored to pending — record the payout in the EOSB dialog.' : ''),
      });
      setReleaseTarget(null); setReleasedTo(''); setReleaseNotes('');
      load();
    } catch (e) {
      toast({ title: 'Release failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setReleasing(false);
    }
  };

  const t = data.totals || {};
  const rows = data.rows || [];
  const due = rows.filter((r) => r.cancellation_due && !r.visa_cancelled);

  return (
    <div className="p-6 space-y-6" data-testid="staff-vault-page">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-teal-100"><Vault className="h-6 w-6 text-teal-700" /></div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Staff Vault</h1>
          <p className="text-sm text-gray-500">Unclaimed dues held "with company" for absconding / imprisoned staff.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard testid="vault-total-held" label="Total held (with company)" value={aed(t.total_held)} accent="text-teal-700" />
        <StatCard testid="vault-count" label="Staff in vault" value={t.count || 0} />
        <StatCard testid="vault-eosb" label="EOSB held" value={aed(t.eosb)} />
        <StatCard testid="vault-due" label="Visas due for cancellation" value={t.cancellation_due || 0} accent={(t.cancellation_due || 0) > 0 ? 'text-amber-600' : 'text-gray-900'} />
      </div>

      {(t.released_count || 0) > 0 && (
        <p className="text-xs text-gray-500 -mt-2" data-testid="vault-released-note">
          {t.released_count} entry(s) released ({aed(t.released_total)}) — no longer held with company.
        </p>
      )}

      {due.length > 0 && (
        <Card className="border-amber-300 bg-amber-50" data-testid="vault-cancellation-due-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base text-amber-800">
              <AlertTriangle className="h-5 w-5" /> Visas due for cancellation ({due.length})
            </CardTitle>
            <p className="text-xs text-amber-700">The 6-month freeze has elapsed. Cancel the visa to set status to "Visa Cancelled Outside Country".</p>
          </CardHeader>
          <CardContent className="space-y-2">
            {due.map((r) => (
              <div key={r.id} className="flex items-center justify-between bg-white rounded-lg border p-2.5" data-testid={`due-row-${r.employee_code}`}>
                <div>
                  <span className="font-medium">{r.employee_name}</span>
                  <span className="text-xs text-gray-400 ml-2">{r.employee_code}</span>
                  <span className="text-xs text-gray-500 ml-2">freeze ended {r.visa_freeze_until}</span>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" className="bg-amber-600 hover:bg-amber-700 h-8" data-testid={`cancel-visa-btn-${r.employee_code}`}>
                      {cancelling === r.employee_id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Cancel Visa'}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Cancel visa for {r.employee_name}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This sets the employment status to "Visa Cancelled Outside Country" and marks the vault hold as visa-cancelled. The held dues remain with the company.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Back</AlertDialogCancel>
                      <AlertDialogAction onClick={() => cancelVisa(r.employee_id)} className="bg-amber-600 hover:bg-amber-700">Confirm Cancellation</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card data-testid="vault-table-card">
        <CardHeader><CardTitle className="text-base">Vault Holdings</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-10">No staff in the vault yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs text-gray-500">
                    <th className="p-2">Employee</th>
                    <th className="p-2">Reason</th>
                    <th className="p-2 text-right">EOSB</th>
                    <th className="p-2 text-right">Leave Salary</th>
                    <th className="p-2 text-right">Pending Salary</th>
                    <th className="p-2 text-right">Total Held</th>
                    <th className="p-2">Freeze Until</th>
                    <th className="p-2 text-center">Status</th>
                    <th className="p-2 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b hover:bg-gray-50" data-testid={`vault-row-${r.employee_code}`}>
                      <td className="p-2">
                        <span className="font-medium">{r.employee_name}</span>
                        <span className="text-xs text-gray-400 ml-2">{r.employee_code}</span>
                      </td>
                      <td className="p-2"><Badge variant="outline">{r.reason_label}</Badge></td>
                      <td className="p-2 text-right">{aed(r.eosb_amount)}</td>
                      <td className="p-2 text-right">{aed(r.leave_salary)}</td>
                      <td className="p-2 text-right">{aed(r.pending_salary)}</td>
                      <td className="p-2 text-right font-semibold text-teal-700">{aed(r.total_amount)}</td>
                      <td className="p-2">{r.visa_freeze_until}</td>
                      <td className="p-2 text-center">
                        {r.released ? (
                          <Badge className="bg-emerald-100 text-emerald-800 gap-1" title={r.released_to ? `Released to ${r.released_to}` : ''}><PackageCheck className="h-3 w-3" /> Released</Badge>
                        ) : r.visa_cancelled ? (
                          <Badge className="bg-gray-200 text-gray-700 gap-1"><CheckCircle2 className="h-3 w-3" /> Visa Cancelled</Badge>
                        ) : r.cancellation_due ? (
                          <Badge className="bg-amber-100 text-amber-800 gap-1"><AlertTriangle className="h-3 w-3" /> Cancellation Due</Badge>
                        ) : (
                          <Badge className="bg-teal-100 text-teal-800 gap-1"><Clock className="h-3 w-3" /> With Company</Badge>
                        )}
                      </td>
                      <td className="p-2 text-center">
                        {r.released ? (
                          <span className="text-xs text-gray-400">{r.released_to || '—'}</span>
                        ) : (
                          <Button size="sm" variant="outline" className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            onClick={() => { setReleaseTarget(r); setReleasedTo(''); setReleaseNotes(''); }}
                            data-testid={`release-btn-${r.employee_code}`}>
                            Release
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!releaseTarget} onOpenChange={(o) => { if (!o && !releasing) setReleaseTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="release-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PackageCheck className="h-5 w-5 text-emerald-600" /> Release from Vault</DialogTitle>
          </DialogHeader>
          {releaseTarget && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Release <b>{releaseTarget.employee_name}</b>'s held dues ({aed(releaseTarget.total_amount)}). This marks the amount as paid out / claimed — it leaves the "with company" totals but is kept for audit.
              </p>
              <div>
                <Label className="text-xs">Released to <span className="text-gray-400">(optional — name of claimant)</span></Label>
                <Input value={releasedTo} onChange={(e) => setReleasedTo(e.target.value)} placeholder="e.g. employee / next of kin / authority" data-testid="release-to-input" />
              </div>
              <div>
                <Label className="text-xs">Notes <span className="text-gray-400">(optional)</span></Label>
                <Input value={releaseNotes} onChange={(e) => setReleaseNotes(e.target.value)} placeholder="Cheque no., reference, etc." data-testid="release-notes-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReleaseTarget(null)} disabled={releasing}>Cancel</Button>
            <Button onClick={submitRelease} disabled={releasing} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid="release-confirm-btn">
              {releasing ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackageCheck className="h-4 w-4" />} Confirm Release
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffVault;
